import React, { useEffect, useState } from "react";
import { useToast } from "../context/ToastContext";
import {
  getBranches, createBranch, bulkAssignHistoricalBranch, getBranchEmployees,
  setEmployeeHomeBranch, setBranchesConfig,
} from "../api";

const NAVY = "#0f172a";
const PRIMARY = "#1e3a8a";

const STEPS = ["Add locations", "Past expenses", "Assign your team"];

const primaryBtnStyle: React.CSSProperties = {
  background: PRIMARY, color: "white", border: "none",
  padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};

type BranchRow = { name: string; tax_branch_no: string; address: string };

export default function EnableBranchesWizard({ onClose, onComplete }: { onClose: () => void; onComplete: () => void }) {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 1: locations to create
  const [rows, setRows] = useState<BranchRow[]>([{ name: "", tax_branch_no: "", address: "" }]);
  const [createdBranchIds, setCreatedBranchIds] = useState<{ id: number; name: string }[]>([]);

  // Step 2: bulk-assign historical expenses
  const [assignHistorical, setAssignHistorical] = useState<"assign" | "leave">("assign");
  const [assignToBranchId, setAssignToBranchId] = useState<number | null>(null);

  // Step 3: team assignment
  const [employees, setEmployees] = useState<{ id: number; name: string; email: string; role: string; branch_id: number | null }[]>([]);
  const [savingEmpId, setSavingEmpId] = useState<number | null>(null);

  function updateRow(i: number, patch: Partial<BranchRow>) {
    setRows(r => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }
  function addRow() {
    setRows(r => [...r, { name: "", tax_branch_no: "", address: "" }]);
  }
  function removeRow(i: number) {
    setRows(r => r.filter((_, idx) => idx !== i));
  }

  async function handleStep1Next() {
    const valid = rows.filter(r => r.name.trim());
    if (!valid.length) { toast("Add at least one location (include your current shop)", "error"); return; }
    setSaving(true);
    let firstBranchId = assignToBranchId;
    try {
      for (const r of valid) {
        const b = await createBranch({ name: r.name.trim(), tax_branch_no: r.tax_branch_no || undefined, address: r.address || undefined });
        // Record success and drop the row immediately — if a later row in this same
        // batch fails, retrying "Continue" must not recreate branches that already
        // succeeded (rows state only ever holds what's still pending).
        setCreatedBranchIds(prev => [...prev, { id: b.id, name: b.name }]);
        setRows(prevRows => prevRows.filter(row => row !== r));
        if (firstBranchId == null) {
          firstBranchId = b.id;
          setAssignToBranchId(b.id);
        }
      }
      setStep(1);
    } catch (err: any) {
      toast(err?.response?.data?.detail || "Could not save all locations — the ones already added are kept; fix and retry the rest", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleStep2Next() {
    setSaving(true);
    try {
      if (assignHistorical === "assign" && assignToBranchId != null) {
        await bulkAssignHistoricalBranch(assignToBranchId);
      }
      const { employees: emps } = await getBranchEmployees();
      setEmployees(emps);
      setStep(2);
    } catch (err: any) {
      toast(err?.response?.data?.detail || "Could not continue — please try again", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleFinish() {
    setSaving(true);
    try {
      await setBranchesConfig(true);
      toast("Branch tracking enabled", "success");
      onComplete();
    } catch (err: any) {
      toast(err?.response?.data?.detail || "Could not enable branch tracking — please try again", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
      zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{
        background: "white", borderRadius: 12, padding: "28px 32px", width: 560, maxWidth: "95vw",
        maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
      }}>
        {/* Progress dots */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
          {STEPS.map((label, i) => (
            <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: 1 }}>
              <div style={{
                width: 24, height: 24, borderRadius: "50%",
                background: i < step ? "#16a34a" : i === step ? PRIMARY : "#e2e8f0",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, color: i <= step ? "white" : "#94a3b8",
              }}>{i < step ? "✓" : i + 1}</div>
              <span style={{ fontSize: 11, color: i <= step ? NAVY : "#94a3b8", fontWeight: i === step ? 600 : 400, textAlign: "center" }}>
                {label}
              </span>
            </div>
          ))}
        </div>

        {step === 0 && (
          <div>
            <h3 style={{ margin: "0 0 6px", color: NAVY, fontSize: 16 }}>Add all your locations</h3>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
              Include your current shop as the first location so past and future expenses stay comparable.
            </p>
            {rows.map((row, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr auto", gap: 8, marginBottom: 8, alignItems: "center" }}>
                <input placeholder="Location name (e.g. Sukhumvit)" value={row.name} onChange={e => updateRow(i, { name: e.target.value })} style={inputStyle} />
                <input placeholder="สาขาที่ (optional)" value={row.tax_branch_no} onChange={e => updateRow(i, { tax_branch_no: e.target.value })} style={inputStyle} />
                <input placeholder="Address (optional)" value={row.address} onChange={e => updateRow(i, { address: e.target.value })} style={inputStyle} />
                {rows.length > 1 && (
                  <button onClick={() => removeRow(i)} style={{ ...outlineBtnStyle, color: "#dc2626", padding: "6px 10px" }}>✕</button>
                )}
              </div>
            ))}
            <button onClick={addRow} style={{ ...outlineBtnStyle, marginTop: 4, marginBottom: 20 }}>+ Add another location</button>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={onClose} style={outlineBtnStyle}>Cancel</button>
              <button onClick={handleStep1Next} disabled={saving} style={primaryBtnStyle}>
                {saving ? "Saving…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h3 style={{ margin: "0 0 6px", color: NAVY, fontSize: 16 }}>Your past expenses</h3>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
              You have existing expense captures with no location tag. What should happen to them?
            </p>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12, cursor: "pointer" }}>
              <input type="radio" checked={assignHistorical === "assign"} onChange={() => setAssignHistorical("assign")} style={{ marginTop: 3 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>Assign all to
                  <select value={assignToBranchId ?? ""} onChange={e => setAssignToBranchId(Number(e.target.value))}
                    style={{ ...inputStyle, display: "inline-block", width: "auto", marginLeft: 8 }}>
                    {createdBranchIds.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select> (recommended)
                </div>
              </div>
            </label>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 20, cursor: "pointer" }}>
              <input type="radio" checked={assignHistorical === "leave"} onChange={() => setAssignHistorical("leave")} style={{ marginTop: 3 }} />
              <div style={{ fontSize: 13, color: NAVY }}>Leave as head office — branch comparison starts today</div>
            </label>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <button onClick={() => setStep(0)} style={outlineBtnStyle}>Back</button>
              <button onClick={handleStep2Next} disabled={saving} style={primaryBtnStyle}>
                {saving ? "Saving…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h3 style={{ margin: "0 0 6px", color: NAVY, fontSize: 16 }}>Assign your team</h3>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
              Staff see only their own location's expenses. Unassigned staff are treated as head office and can see everything.
            </p>
            <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
              {employees.map(emp => (
                <div key={emp.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1px solid #f1f5f9" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{emp.name} <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: 11 }}>({emp.role})</span></div>
                    {emp.branch_id == null && <div style={{ fontSize: 11, color: "#b45309" }}>⚠ Not assigned</div>}
                  </div>
                  <select
                    value={emp.branch_id ?? ""}
                    disabled={savingEmpId === emp.id}
                    onChange={async e => {
                      const branch_id = e.target.value ? Number(e.target.value) : null;
                      setEmployees(es => es.map(x => (x.id === emp.id ? { ...x, branch_id } : x)));
                      setSavingEmpId(emp.id);
                      try { await setEmployeeHomeBranch(emp.id, branch_id); }
                      catch { toast("Could not update assignment", "error"); }
                      setSavingEmpId(null);
                    }}
                    style={{ ...inputStyle, width: 160, opacity: savingEmpId === emp.id ? 0.6 : 1 }}
                  >
                    <option value="">Head office (all)</option>
                    {createdBranchIds.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 12, color: "#94a3b8", margin: "12px 0 20px" }}>
              You can change any of this later in the Settings tab.
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <button onClick={() => setStep(1)} style={outlineBtnStyle}>Back</button>
              <button onClick={handleFinish} disabled={saving} style={{ ...primaryBtnStyle, background: "#16a34a" }}>
                {saving ? "Finishing…" : "Enable branch tracking"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
